import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Heart, TriangleAlert } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useConditions, useTemperaments } from "@/hooks/useLookups";
import { cn } from "@/lib/utils";
import { useUpdatePetConditions } from "../usePetProfileMutations";
import type { PetCondition } from "../usePetReservationSummary";
import { PetProfileSectionEmpty, PetProfileSectionShell } from "../PetProfileSectionShell";

export type PetConditionsSectionProps = {
  petId: string;
  kind: "allergy" | "temperament";
  allergies: PetCondition[];
  temperaments: PetCondition[];
  compact?: boolean;
  showEmpty?: boolean;
};

const COPY = {
  allergy: {
    title: "petProfile.allergiesLabel",
    edit: "petProfile.editAllergies",
    done: "petProfile.doneAllergies",
    empty: "petProfile.allergiesEmpty",
    icon: TriangleAlert,
    tone: "amber",
    chip: "border-amber-200 bg-amber-50 text-amber-800",
    chipSelected: "border-amber-300 bg-amber-100 text-amber-800",
  },
  temperament: {
    title: "petProfile.temperamentsLabel",
    edit: "petProfile.editTemperaments",
    done: "petProfile.doneTemperaments",
    empty: "petProfile.temperamentsEmpty",
    icon: Heart,
    tone: "blue",
    chip: "border-blue-200 bg-blue-50 text-blue-700",
    chipSelected: "border-blue-300 bg-blue-100 text-blue-700",
  },
} as const;

export function PetConditionsSection({ petId, kind, allergies, temperaments, compact, showEmpty }: PetConditionsSectionProps) {
  const { t } = useTranslation("daycareHotel");
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const updateConditions = useUpdatePetConditions();
  const { data: allergyOptions } = useConditions("allergy");
  const { data: temperamentOptions } = useTemperaments();

  const copy = COPY[kind];
  const isAllergy = kind === "allergy";
  const selected = isAllergy ? allergies : temperaments;
  const options = (isAllergy ? allergyOptions : temperamentOptions) ?? [];
  const selectedIds = selected.map((condition) => condition.id);

  if (compact && !showEmpty && !isEditing && selected.length === 0) return null;

  const toggle = async (conditionId: string) => {
    const nextIds = selectedIds.includes(conditionId)
      ? selectedIds.filter((id) => id !== conditionId)
      : [...selectedIds, conditionId];

    try {
      await updateConditions.mutateAsync({
        petId,
        allergyIds: isAllergy ? nextIds : allergies.map((condition) => condition.id),
        temperamentIds: isAllergy ? temperaments.map((condition) => condition.id) : nextIds,
      });
    } catch {
      toast({ title: t("petProfile.saveError"), variant: "destructive" });
    }
  };

  return (
    <PetProfileSectionShell
      title={t(copy.title)}
      icon={copy.icon}
      tone={copy.tone}
      count={selected.length}
      isEditing={isEditing}
      onToggleEdit={() => setIsEditing((current) => !current)}
      editLabel={t(copy.edit)}
      doneLabel={t(copy.done)}
      compact={compact && !isEditing}
    >
      {isEditing ? (
        <div className="flex flex-wrap gap-1.5">
          {options.map((option) => {
            const active = selectedIds.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                aria-pressed={active}
                onClick={() => void toggle(option.id)}
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                  active
                    ? copy.chipSelected
                    : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground",
                )}
              >
                {active ? <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : null}
                {option.name}
              </button>
            );
          })}
        </div>
      ) : selected.length === 0 ? (
        <PetProfileSectionEmpty>{t(copy.empty)}</PetProfileSectionEmpty>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((condition) => (
            <span
              key={condition.id}
              className={cn("rounded-full border px-2 py-1 text-xs font-medium", copy.chip)}
            >
              {condition.name}
            </span>
          ))}
        </div>
      )}
    </PetProfileSectionShell>
  );
}
